/**
 * 사람 목록의 차례 — **직책 → 직위 → 이름** (2026-08-31 · 사용자 지시).
 *
 * 사용자 지시: 「조직도나 정렬에서 **직책, 직위 정렬**로 정렬해야 함. **대표가 젤 위로** 올라와야 함.」
 *
 * ── 왜 이름순이면 안 되나 ────────────────────────────────────────────────
 * ★★★ 이름순은 **조직을 못 보여 준다.** 회사 직속 셋이 「admin · AI Bot · 김철희」로 서면,
 *    맨 아래 있는 사람이 **대표**라는 것을 화면 어디서도 알 수 없다. 조직도는 «누가 위인가»를
 *    보여 주는 자리다.
 *
 * ── 차례를 정하는 두 축 ──────────────────────────────────────────────────
 * ★★ **직책이 직위를 이긴다.** 「과장인데 팀장」이 「차장인데 팀원」보다 위다 — 직책은 지금
 *    맡은 자리이고 직위는 서열이기 때문이다(`project-hr-rank-vs-role` 에서 이미 확정한 규칙).
 *    그래서 직책을 먼저 보고, 같은 직책 안에서 직위로 가른다.
 * ★ **직위는 클수록 높다**(`hr_job_grades.level` — 대표이사 18 · 이사 13 · 사원 5).
 *   모르는 직위(목록 밖 · 비어 있음)는 **맨 아래**로 보낸다 — 지어낸 자리에 끼우지 않는다.
 * ★ 마지막은 이름 — 같은 자리끼리는 차례가 흔들리지 않아야 한다(같은 목록을 두 번 열면
 *   순서가 달라지는 것만큼 못 믿을 화면이 없다).
 */

/** 직책의 차례 — **작을수록 위**. 화면이 아는 만큼만 쓴다. */
export const DUTY_ORDER = {
  /** 대표 — 결재 사다리의 마지막 칸이자 조직의 꼭대기. */
  REPRESENTATIVE: 0,
  /** 부서장(`organization_divisions.leaderUserId`). */
  DIVISION_HEAD: 1,
  /** 팀장(`team_members.isLeader`). */
  TEAM_LEAD: 2,
  /** 그 밖 — 직책이 없는 사람. */
  MEMBER: 3,
} as const;

/** 직위를 모를 때의 값 — 어떤 실제 급보다도 낮다(맨 아래로 간다). */
export const UNKNOWN_LEVEL = -1;

export interface OrderablePerson {
  id: string;
  name: string;
  /** 직위 이름(`users.position`). 비어 있을 수 있다. */
  position?: string | null;
}

export interface PeopleOrderContext {
  /** 이 사람의 직책 차례 — `DUTY_ORDER` 중 하나. */
  dutyOrderOf: (person: OrderablePerson) => number;
  /** 직위 이름 → 급(클수록 높다). 모르면 `UNKNOWN_LEVEL`. */
  levelOf: (position: string | null | undefined) => number;
}

/** 직책 → 직위 → 이름. `Array.prototype.sort` 에 그대로 넘긴다. */
export function comparePeople(
  a: OrderablePerson,
  b: OrderablePerson,
  ctx: PeopleOrderContext,
): number {
  const duty = ctx.dutyOrderOf(a) - ctx.dutyOrderOf(b);
  if (duty !== 0) return duty;
  // 직위는 **큰 쪽이 위**라 뺄셈 방향이 반대다.
  const level = ctx.levelOf(b.position) - ctx.levelOf(a.position);
  if (level !== 0) return level;
  return a.name.localeCompare(b.name, 'ko');
}

/** 목록 하나를 그 차례로 정렬한 **새 배열**을 준다(원본을 건드리지 않는다). */
export function sortPeople<T extends OrderablePerson>(
  people: readonly T[],
  ctx: PeopleOrderContext,
): T[] {
  return [...people].sort((a, b) => comparePeople(a, b, ctx));
}
